/**
 * Site scanner API client.
 *
 * One place that knows the endpoint shapes, so a component never builds a query
 * string by hand. `toQuery` is the only interesting part: the table's filter
 * state is a plain object with optional fields, and everything absent has to
 * stay absent from the URL rather than turning into `undefined`.
 */
import { api } from "../api";
import type {
  ScanFacets,
  ScanListParams,
  ScanListResult,
  SiteScan,
  VersionReference,
} from "./types";

/** Filter state -> query string. Skips empty values, joins arrays with commas. */
export function toQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value == null || value === "") continue;
    if (Array.isArray(value)) {
      if (!value.length) continue;
      search.set(key, value.join(","));
    } else if (typeof value === "boolean") {
      search.set(key, value ? "1" : "0");
    } else {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

/** Scan any address (URL or bare host). */
export const scanSite = (url: string, refresh = false): Promise<SiteScan> =>
  api.get<SiteScan>(`/admin/ariregister/scan?url=${encodeURIComponent(url)}${refresh ? "&refresh=1" : ""}`);

/** Scan the website stored on a register entity (`ArEntity.webUrl`). */
export const scanEntitySite = (regCode: string, refresh = false): Promise<SiteScan> =>
  api.get<SiteScan>(
    `/admin/ariregister/${encodeURIComponent(regCode)}/scan${refresh ? "?refresh=1" : ""}`,
  );

/** The scans table. */
export const listScans = (params: ScanListParams = {}): Promise<ScanListResult> =>
  api.get<ScanListResult>(`/admin/ariregister/scans${toQuery(params as Record<string, unknown>)}`);

/** Counts for the filter chips, over the whole table rather than the filter. */
export const scanFacets = (): Promise<ScanFacets> => api.get<ScanFacets>("/admin/ariregister/scans/facets");

/**
 * What a pasted list started. `excluded` names the sites left out because they
 * are on the do-not-scan list; an older server sends none.
 */
export interface BulkScanStarted {
  accepted: number;
  domains: string[];
  excluded?: string[];
}

/** Paste a list of addresses; they are scanned in the background. */
export const bulkScan = (input: string, refresh = false): Promise<BulkScanStarted> =>
  api.post<BulkScanStarted>("/admin/ariregister/scans/bulk", { input, refresh });

export const deleteScan = (domain: string): Promise<{ ok: boolean }> =>
  api.del<{ ok: boolean }>(`/admin/ariregister/scans/${encodeURIComponent(domain)}`);

/**
 * URL for the CSV export. A plain link rather than a fetch: the browser sends
 * the session cookie with it and handles the download itself, which is one less
 * blob to keep in memory.
 */
export const scansCsvUrl = (params: ScanListParams = {}): string =>
  `/api/admin/ariregister/scans/export.csv${toQuery(params as Record<string, unknown>)}`;

/** How current our own "latest version" reference is. */
export const versionReference = (): Promise<VersionReference> =>
  api.get<VersionReference>("/admin/ariregister/versions");

export const refreshVersionReference = (): Promise<{ ok: boolean; latest: string | null }> =>
  api.post<{ ok: boolean; latest: string | null }>("/admin/ariregister/versions/refresh", {});
